import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, BoxSeam, CheckLg, PlusLg, Search, Trash3, XLg } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { ROUNDS_MAX, ROUND_PRODUCTS_MAX } from '../prizeWheelShape';
import { formatPrice, totalValue } from './wheelProducts';
import './GamePrizes.scss';

// The prize wheel's rounds (contract v2): 1..10 rounds, each = one spin = one
// winner who gets ALL of the round's products (quantity 1 each). Products come
// from the brand's own catalogue; a round can be reordered or removed.

let keySeq = 0;
export const newRound = (productIds = []) => ({ key: `r${++keySeq}`, productIds });

export function Thumb({ product }) {
  return (
    <span className="wheel-thumb" aria-hidden="true">
      {product?.imageUrl ? <img src={product.imageUrl} alt="" loading="lazy" /> : <BoxSeam size={14} />}
    </span>
  );
}

/** Search the catalogue and tick products into one round (also the gift drop's prize picker). */
export function ProductPicker({ catalogue, selected, onToggle, onClose, disabled, max = ROUND_PRODUCTS_MAX }) {
  const { t, lang } = useLocale();
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    if (!Array.isArray(catalogue)) return catalogue;
    const q = query.trim().toLowerCase();
    return q ? catalogue.filter((p) => p.name.toLowerCase().includes(q)) : catalogue;
  }, [catalogue, query]);
  return (
    <div className="wheel-picker">
      <div className="wheel-picker__search">
        <Search size={13} aria-hidden="true" />
        <input
          type="search"
          value={query}
          placeholder={t('wheel.form.productSearch')}
          aria-label={t('wheel.form.productSearch')}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
      </div>
      {catalogue === null ? (
        <div className="panel-card__muted wheel-picker__msg">{t('shell.loading')}</div>
      ) : catalogue === 'error' ? (
        <div className="wheel-form__err wheel-picker__msg">{t('wheel.form.productsError')}</div>
      ) : shown.length === 0 ? (
        <div className="panel-card__muted wheel-picker__msg">{t(catalogue.length ? 'wheel.form.productsNoMatch' : 'wheel.form.productsNone')}</div>
      ) : (
        <ul className="wheel-picker__list">
          {shown.map((p) => {
            const on = selected.includes(p.id);
            const status = String(p.status || '').toUpperCase();
            const blocked = !on && (p.available === false || selected.length >= max);
            return (
              <li key={p.id}>
                <label className={`wheel-picker__row${on ? ' is-on' : ''}${blocked ? ' is-blocked' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => onToggle(p.id)} disabled={disabled || blocked} />
                  <span className="wheel-picker__check" aria-hidden="true">{on && <CheckLg size={12} />}</span>
                  <Thumb product={p} />
                  <span className="wheel-picker__name">{p.name || '—'}</span>
                  {p.available === false ? (
                    <span className="wheel-picker__status is-out">{t('wheel.form.productOut')}</span>
                  ) : (
                    status && status !== 'ACTIVE' && <span className="wheel-picker__status">{status}</span>
                  )}
                  <span className="wheel-picker__price">{formatPrice(p, lang)}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <div className="wheel-picker__foot">
        <span className="gift-muted">{t('wheel.form.productsPicked', { n: selected.length, max })}</span>
        <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={onClose}>
          {t('wheel.form.productsDone')}
        </button>
      </div>
    </div>
  );
}

/**
 *   rounds     [{ key, productIds }]
 *   products   Map id → product (catalogue + the wheel's own, for edit)
 *   catalogue  product[] | null (loading) | 'error'
 */
export default function RoundsBuilder({ rounds, onChange, products, catalogue, disabled, invalid }) {
  const { t, lang } = useLocale();
  const [open, setOpen] = useState(() => (rounds.length === 1 && rounds[0].productIds.length === 0 ? rounds[0].key : null));

  const set = (i, patch) => onChange(rounds.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const move = (i, d) => {
    const next = [...rounds];
    const [r] = next.splice(i, 1);
    next.splice(i + d, 0, r);
    onChange(next);
  };
  const toggle = (i, id) => {
    const ids = rounds[i].productIds;
    set(i, { productIds: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  };
  const add = () => {
    const r = newRound();
    onChange([...rounds, r]);
    setOpen(r.key);
  };

  return (
    <div className="wheel-rounds">
      <ol className="wheel-rounds__list">
        {rounds.map((r, i) => {
          const items = r.productIds.map((id) => products.get(id) || { id, name: id, imageUrl: null, price: null });
          const empty = items.length === 0;
          return (
            <li key={r.key} className={`wheel-round${invalid && empty ? ' is-invalid' : ''}`}>
              <div className="wheel-round__head">
                <span className="wheel-round__num" aria-hidden="true">{i + 1}</span>
                <div className="wheel-round__title">
                  <strong>{t('wheel.round', { n: i + 1 })}</strong>
                  <span className="gift-muted">
                    {empty ? t('wheel.form.roundEmptyHint') : `${t('wheel.form.productsN', { n: items.length })} · ${totalValue(items, lang)}`}
                  </span>
                </div>
                <div className="wheel-round__tools">
                  <button type="button" onClick={() => move(i, -1)} disabled={disabled || i === 0} aria-label={t('wheel.form.roundUp', { n: i + 1 })} title={t('wheel.form.roundUp', { n: i + 1 })}>
                    <ArrowUp size={13} />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={disabled || i === rounds.length - 1} aria-label={t('wheel.form.roundDown', { n: i + 1 })} title={t('wheel.form.roundDown', { n: i + 1 })}>
                    <ArrowDown size={13} />
                  </button>
                  <button type="button" onClick={() => onChange(rounds.filter((x) => x !== r))} disabled={disabled || rounds.length === 1} aria-label={t('wheel.form.roundRemove', { n: i + 1 })} title={t('wheel.form.roundRemove', { n: i + 1 })}>
                    <Trash3 size={13} />
                  </button>
                </div>
              </div>
              {!empty && (
                <ul className="wheel-round__products">
                  {items.map((p) => (
                    <li key={p.id} className="wheel-product">
                      <Thumb product={p} />
                      <span className="wheel-product__name" title={p.name}>{p.name}</span>
                      <span className="wheel-product__price">{formatPrice(p, lang)}</span>
                      <button type="button" className="wheel-product__remove" onClick={() => toggle(i, p.id)} disabled={disabled} aria-label={t('wheel.form.productRemove', { name: p.name })}>
                        <XLg size={11} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {open === r.key ? (
                <ProductPicker
                  catalogue={catalogue}
                  selected={r.productIds}
                  onToggle={(id) => toggle(i, id)}
                  onClose={() => setOpen(null)}
                  disabled={disabled}
                />
              ) : (
                <button type="button" className="wheel-round__add" onClick={() => setOpen(r.key)} disabled={disabled}>
                  <PlusLg size={12} /> {t(empty ? 'wheel.form.productsAdd' : 'wheel.form.productsEdit')}
                </button>
              )}
            </li>
          );
        })}
      </ol>
      <button type="button" className="wheel-rounds__add" onClick={add} disabled={disabled || rounds.length >= ROUNDS_MAX}>
        <PlusLg size={13} /> {t('wheel.form.roundAdd')}
        <span className="gift-muted">{rounds.length}/{ROUNDS_MAX}</span>
      </button>
    </div>
  );
}
